import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `driveSettings:` scope (F6), following the
 * exact `documents:` precedent (`apps/web/src/routes/documents/
 * documents.i18n.ts`) — NOT a second i18n runtime (E18/§13.3 ban), just
 * physically scoped here because `packages/i18n/**` is outside this task's
 * ownership boundary.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/driveSettings.json`.
 */
const en = {
  'title': 'Google Drive',
  'description': 'Connect Google Drive to browse and import documents into your explorer library.',
  'devStub.notice': 'Running in dev mode — Drive connect may use a stubbed consent flow.',
  'status.loading': 'Checking your connection…',
  'status.error.title': 'Couldn’t load your Drive connection status.',
  'status.connected.title': 'Connected',
  'status.connected.description': 'Signed in as {email}.',
  'status.connected.expired': 'Your Drive connection expired — reconnect to keep importing files.',
  'status.disconnected.title': 'Not connected',
  'status.disconnected.description': 'Connect your Google account to import files directly from Drive.',
  'action.connect': 'Connect Google Drive',
  'action.reconnect': 'Reconnect',
  'action.disconnect': 'Disconnect',
  'action.import': 'Import from Drive',
  'action.retry': 'Retry',
  'disconnect.confirm': 'Disconnect',
  'disconnect.note': 'Files already imported from Drive stay in your library — disconnecting only stops new imports.',
  'toast.disconnected.action': 'Disconnected',
  'picker.title': 'Import from Google Drive',
  'picker.description': 'Select files to import into your explorer library.',
  'picker.root': 'My Drive',
  'picker.searchLabel': 'Search Drive',
  'picker.searchPlaceholder': 'Search this folder…',
  'picker.empty': 'This folder is empty.',
  'picker.error.title': 'Couldn’t load Google Drive.',
  'picker.loadMore': 'Load more',
  'picker.selectedCount': '{count, plural, one {# file selected} other {# files selected}}',
  'picker.import': 'Import selected',
  'picker.cancel': 'Cancel',
  'picker.close': 'Close',
  'import.outcome.queued': 'Queued for import',
  'import.outcome.duplicate': 'Already imported (skipped)',
  'import.outcome.unsupported_type': 'Unsupported file type (skipped)',
  'import.resultsTitle': 'Import results',
  'import.done': 'Done',
} as const;

const ro: Record<keyof typeof en, string> = {
  'title': 'Google Drive',
  'description': 'Conectează Google Drive pentru a răsfoi și importa documente în biblioteca ta din explorator.',
  'devStub.notice': 'Rulează în modul dev — conectarea la Drive poate folosi un flux de consimțământ simulat.',
  'status.loading': 'Se verifică conexiunea…',
  'status.error.title': 'Nu am putut încărca starea conexiunii Drive.',
  'status.connected.title': 'Conectat',
  'status.connected.description': 'Autentificat ca {email}.',
  'status.connected.expired': 'Conexiunea Drive a expirat — reconectează-te pentru a continua importurile.',
  'status.disconnected.title': 'Neconectat',
  'status.disconnected.description': 'Conectează-ți contul Google pentru a importa fișiere direct din Drive.',
  'action.connect': 'Conectează Google Drive',
  'action.reconnect': 'Reconectează',
  'action.disconnect': 'Deconectează',
  'action.import': 'Importă din Drive',
  'action.retry': 'Reîncearcă',
  'disconnect.confirm': 'Deconectează',
  'disconnect.note': 'Fișierele deja importate din Drive rămân în biblioteca ta — deconectarea oprește doar importurile noi.',
  'toast.disconnected.action': 'Deconectat',
  'picker.title': 'Importă din Google Drive',
  'picker.description': 'Selectează fișiere de importat în biblioteca ta din explorator.',
  'picker.root': 'Drive-ul meu',
  'picker.searchLabel': 'Caută în Drive',
  'picker.searchPlaceholder': 'Caută în acest folder…',
  'picker.empty': 'Acest folder este gol.',
  'picker.error.title': 'Nu am putut încărca Google Drive.',
  'picker.loadMore': 'Încarcă mai multe',
  'picker.selectedCount': '{count, plural, one {# fișier selectat} few {# fișiere selectate} other {# de fișiere selectate}}',
  'picker.import': 'Importă selecția',
  'picker.cancel': 'Anulează',
  'picker.close': 'Închide',
  'import.outcome.queued': 'În coadă pentru import',
  'import.outcome.duplicate': 'Deja importat (omis)',
  'import.outcome.unsupported_type': 'Tip de fișier neacceptat (omis)',
  'import.resultsTitle': 'Rezultatele importului',
  'import.done': 'Gata',
};

export const DRIVE_SETTINGS_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

export type DriveSettingsCatalogKey = keyof typeof en;
